import { Button } from '@/shared/ui';
import { useWorkspaceLabel } from '@/shared/platform';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { NotFoundInPage } from '@/components/NotFoundInPage';
import { MOCK_NOW } from '@/components/lists';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { InsurancePageView } from '@/features/patients/insurance/InsurancePageView';
import type { InsurancePatient, InsurancePlan } from '@/features/patients/insurance/types';
import { getPatient, type PatientRow } from '@/features/patients/directory/patientsMock';

/** Whole years from a dob (ISO) to the mock "today". */
function ageFrom(iso: string): number {
  const dob = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(dob.getTime())) return 0;
  let a = MOCK_NOW.getFullYear() - dob.getFullYear();
  const m = MOCK_NOW.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && MOCK_NOW.getDate() < dob.getDate())) a--;
  return a;
}

function initialsOf(name: string): string {
  const parts = name.split(' ');
  const a = parts[0]?.[0] ?? '';
  const b = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return `${a}${b}`.toUpperCase();
}

/** Build the identity override so the insurance page renders THIS patient. */
function identityFor(row: PatientRow): { patient: InsurancePatient; primaryPlan: InsurancePlan } {
  const [first = row.name, ...rest] = row.name.split(' ');
  const last = rest.join('') || first;
  const patient: InsurancePatient = {
    id: row.id,
    name: row.name,
    initials: initialsOf(row.name),
    age: ageFrom(row.dobIso),
    since: 2021,
    status: ['Established · Active'],
    phone: row.phone,
    email: `${first}.${last}@example.com`.toLowerCase(),
    contactPreference: 'prefers sms',
    visits: 12,
    language: 'English',
    nextVisit: {
      at: row.nextVisitAtIso,
      type: row.nextVisitType || 'No upcoming visit',
      provider: row.nextVisitProvider,
    },
  };

  const remaining = row.yearlyMaxLeft ?? 0;
  const annualMax = 1500;
  const used = Math.max(0, annualMax - remaining);
  const primaryPlan: InsurancePlan = {
    id: 'primary',
    order: 'primary',
    carrier: row.primaryPayer || 'No plan on file',
    plan: row.primaryPayer ? 'Dental PPO' : '—',
    type: 'PPO',
    network: 'in',
    memberId: row.memberId || '—',
    group: '—',
    subscriber: row.name,
    relationship: 'Self',
    effective: '2024-01-01',
    payerId: '—',
    phone: '(800) 555-0100',
    annualMax,
    used,
    remaining,
    resets: '2026-12-31',
    status: row.insState === 'verified' ? 'verified' : 'partly_verified',
    sourcesUsed: 5,
  };

  return { patient, primaryPlan };
}

/**
 * RCM patient page — one scrolling insurance page (handoff). `/patients/:id`
 * opens here. The patient is resolved from the directory by the route param so
 * each card opens THAT patient (rcm-24 §6 — the staging bug was that every card
 * opened the single Waylon Baird fixture). The deep clinical detail below the
 * identity remains the shared demo fixture until the backend lands.
 */
export function PatientProfilePage() {
  const workspaceLabel = useWorkspaceLabel();
  const { patientId } = useParams<{ patientId: string }>();
  const [reverifying, setReverifying] = useState(false);

  const row = getPatient(patientId);
  const override = row ? identityFor(row) : null;
  const displayName = row?.name ?? 'Patient not found';

  usePageTopBar({
    breadcrumb: [workspaceLabel, 'Patients', displayName],
    actions: !row ? undefined : (
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          iconLeft="refresh"
          disabled={reverifying}
          onClick={() => {
            setReverifying(true);
            window.setTimeout(() => { setReverifying(false); }, 1600);
          }}
        >
          {reverifying ? 'Re-verifying…' : 'Re-verify all'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          iconLeft="doc"
          onClick={() => {
            // The report is its own rendered page, opened in a new tab (rcm-28 §2).
            window.open(`/patients/${encodeURIComponent(patientId ?? '')}/report`, '_blank', 'noopener,noreferrer');
          }}
        >
          Print eligibility report
        </Button>
      </div>
    ),
    actionsVersion: reverifying ? 'busy' : 'idle',
  });

  // An id that isn't a patient shows the Patients page's empty state, never the demo patient (D202).
  if (!override) {
    return <NotFoundInPage thing="patient" id={patientId ?? ''} listLabel="patients" listTo="/patients" />;
  }
  return <InsurancePageView patient={override.patient} primaryPlan={override.primaryPlan} />;
}
